/* global t, runtime, mergeState, safeArray, num, buildLedgerLines, getReportsModel, deriveReports */
// PERIODEKONTROLLER — delene lesemodellen er satt sammen av
//
// Skilt fra backend-period-recon-view.jsx, som setter dem sammen. Her ligger de
// to som henter tall UTENFOR avstemmingsmappa: fullstendigheten i bokføringen og
// den analytiske sammenlikningen mot et annet grunnlag.
//
// Ingen mockdata. Der appen ikke har et grunnlag, står raden som «ikke
// tilgjengelig» — ikke som 0. Et nulltall betyr «vi har sett etter og fant
// ingenting», og det er noe helt annet enn «vi kan ikke se etter».

// Tallformatet lesemodellen bruker. Det ligger her og ikke i en side: en
// tjeneste skal kunne kjores uten at flaten er lastet.
const pkMoney = (value) => (Number.isFinite(Number(value))
  ? window.PlummeI18n.number(Number(value), { minimumFractionDigits: 0, maximumFractionDigits: 0 })
  : '—');

// Builds an object whose `label` resolves at read time, so module-level tables
// follow the UI language instead of freezing at the first evaluation.
const pkLabeled = (key, fallback, rest) => Object.defineProperty({ ...rest }, 'label', {
  get: () => t(key, fallback), enumerable: true,
});

const pkRound = (value) => Math.round((num(value) + Number.EPSILON) * 100) / 100;

const pkPeriodRange = (periodId) => {
  const [yearText, monthText] = String(periodId || '').split('-');
  const year = Number(yearText);
  const monthIndex = Number(monthText) - 1;
  if (!Number.isFinite(year) || !Number.isFinite(monthIndex) || monthIndex < 0 || monthIndex > 11) return null;
  const last = new Date(year, monthIndex + 1, 0);
  const iso = (date) => date.getFullYear()
    + '-' + String(date.getMonth() + 1).padStart(2, '0')
    + '-' + String(date.getDate()).padStart(2, '0');
  return { year, monthIndex, from: iso(new Date(year, monthIndex, 1)), to: iso(last), days: last.getDate(), iso };
};

const pkDateInMonth = (value, year, monthIndex) => {
  const iso = String(value || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return false;
  return Number(iso.slice(0, 4)) === year && Number(iso.slice(5, 7)) - 1 === monthIndex;
};

// Speiler `ovrigIsManual` i backend-other-recon-view.jsx. En egen regel her
// ville betydd at «manuell postering» var to forskjellige ting avhengig av
// hvilken skjerm man sto på.
const pkIsManual = (line) => /yearend|manual|journal|adjust|årsoppgjør|arsoppgjor/i
  .test(String(line?.sourceType || '') + ' ' + String(line?.kind || ''));

// «Nær periodeslutt» er de fem siste kalenderdagene i måneden. Det er ingen
// regel fra loven — det er utvalget den som kontrollerer bør se på, og tallet
// står her ett sted slik at flaten og teksten ikke kan bli uenige.
const PK_LATE_DAYS = 5;

// ------------------------------------------------------------------
// Fullstendighet og riktig periode
// ------------------------------------------------------------------
// Radene kommer fra kilder appen allerede har: bilagsinnboksen og
// bankavstemmingens egne tellinger. To rader i designet har ingen kilde ennå —
// posteringer datert utenfor perioden og mulige feilperiodiseringer krever et
// mottaks- eller leveransedato-spor Plumme ikke fører. De står som «ikke
// tilgjengelig», ikke som 0: 0 ville vært en påstand om at det er kontrollert.
const buildPeriodCompleteness = ({ periodId, state, bankCounts = {} } = {}) => {
  const range = pkPeriodRange(periodId);
  const inbox = safeArray(state?.receiptInbox).filter((entry) => {
    if (['booked', 'trashed', 'trash'].includes(String(entry?.status || ''))) return false;
    if (!range) return true;
    return pkDateInMonth(
      entry?.receiptAnalysis?.receiptDate || entry?.received || entry?.createdAt,
      range.year, range.monthIndex,
    );
  });
  // Et dokument som er lest, men fortsatt ikke bokført. Det er en DELMENGDE av
  // de uposterte — ikke et tillegg. Summeres de to, dobbelttelles dokumentet, og
  // periodeoversikten viser flere mangler enn det finnes.
  const analysed = inbox.filter((entry) => Boolean(entry?.receiptAnalysis));

  let lateVouchers = 0;
  if (range && typeof buildLedgerLines === 'function') {
    const lateFrom = range.iso(new Date(range.year, range.monthIndex, Math.max(1, range.days - PK_LATE_DAYS + 1)));
    const lines = buildLedgerLines({ from: lateFrom, to: range.to, state }).filter(pkIsManual);
    lateVouchers = new Set(lines.map((line) => line.voucherId)).size;
  }

  return [
    { id: 'inbox', label: t('rcd.comp.inbox', 'Uposterte dokumenter i innboksen'), value: inbox.length, available: true },
    {
      id: 'received', label: t('rcd.comp.received', 'Dokumenter mottatt, men ikke bokført'), value: analysed.length, available: true,
      note: t('rcd.comp.receivedNote', 'Inngår i de uposterte dokumentene over — telles ikke to ganger.'),
    },
    { id: 'bankNoDoc', label: t('rcd.comp.bankNoDoc', 'Banktransaksjoner uten bilag'), value: num(bankCounts.manglerBilag), available: true },
    { id: 'docNoBank', label: t('rcd.comp.docNoBank', 'Bilag uten koblet betaling'), value: num(bankCounts.utenBanktreff), available: true },
    { id: 'duplicates', label: t('rcd.comp.duplicates', 'Duplikatmistenkte bilag'), value: num(bankCounts.muligDuplikat), available: true },
    {
      id: 'outsidePeriod', label: t('rcd.comp.outsidePeriod', 'Posteringer med dato utenfor perioden'), value: null, available: false,
      note: t('rcd.comp.outsidePeriodNote', 'Plumme fører ikke mottaksdato ved siden av bilagsdatoen, og kan derfor ikke skille en riktig etterpostering fra en feildatert.'),
    },
    {
      id: 'lateManual', label: t('rcd.comp.lateManual', 'Manuelle posteringer nær periodeslutt'), value: lateVouchers, available: true,
      note: t('rcd.comp.lateManualNote', 'Manuelle bilag datert de siste {days} dagene i perioden. Et forslag om hva som bør ses på — ikke en påstand om feil.', { days: PK_LATE_DAYS }),
    },
    {
      id: 'cutoff', label: t('rcd.comp.cutoff', 'Mulige feilperiodiseringer'), value: null, available: false,
      note: t('rcd.comp.cutoffNote', 'Krever leveranse- eller mottaksdato ved siden av bilagsdatoen. Plumme har ikke det sporet ennå.'),
    },
  ];
};

// ------------------------------------------------------------------
// Analytisk kontroll
// ------------------------------------------------------------------
// Resultatet for perioden mot et sammenlikningsgrunnlag. Tallene kommer fra
// saldobalansen appen allerede regner ut — ingen egen motor.
//
// Gruppene følger den norske standardkontoplanens hovedgrupper (3 inntekt,
// 4 varekostnad, 5 lønn, 6–8 andre driftskostnader). Det er den SAMME
// inndelingen `TRIAL_GROUPS` i backend-accounting-engine.jsx bruker, ikke en ny
// regel. Er kontoplanen importert eller ikke-norsk, faller radene bort av seg
// selv: gruppen får ingen linjer, og en rad uten grunnlag vises ikke.
//
// Vurderingen av om et avvik er vesentlig gjøres IKKE her. Den kombinerer beløp,
// vesentlighetsgrense og faglig skjønn, og den siste delen kan ikke regnes ut —
// modellen leverer tallene, flaten viser dem, og mennesket konkluderer.
const PK_ANALYTICS_GROUPS = [
  pkLabeled('rcd.group.revenue', 'Omsetning', { key: 'omsetning', section: 'inntekter', digits: ['3'] }),
  pkLabeled('rcd.group.cogs', 'Varekostnad', { key: 'varekostnad', section: 'kostnader', digits: ['4'] }),
  pkLabeled('rcd.group.payroll', 'Lønnskostnad', { key: 'lonnskostnad', section: 'kostnader', digits: ['5'] }),
  pkLabeled('rcd.group.otherOpex', 'Andre driftskostnader', { key: 'andre_driftskostnader', section: 'kostnader', digits: ['6', '7', '8'] }),
];

const pkResultFor = (state, year, monthIndex) => {
  const options = { year, months: [monthIndex, monthIndex], includeChart: false, includeTrial: false };
  try {
    if (state && typeof deriveReports === 'function') return deriveReports(state, year, options)?.result || null;
    return typeof getReportsModel === 'function' ? (getReportsModel(options)?.result || null) : null;
  } catch (error) {
    return null;
  }
};

const pkGroupTotals = (result) => {
  const totals = {};
  for (const group of PK_ANALYTICS_GROUPS) totals[group.key] = { amount: 0, lines: 0 };
  for (const section of safeArray(result?.sections)) {
    for (const line of safeArray(section.lines)) {
      const digit = String(line?.nr || '').trim().charAt(0);
      const group = PK_ANALYTICS_GROUPS.find((entry) => entry.section === section.key && entry.digits.includes(digit));
      if (!group) continue;
      totals[group.key].amount = pkRound(totals[group.key].amount + Math.abs(num(line.amount)));
      totals[group.key].lines += 1;
    }
  }
  return totals;
};

// Budsjettet per måned og gruppe. Finnes bare når klienten faktisk har lagt inn
// et budsjett med kontolinjer — et budsjett uten linjer kan ikke fordeles på
// grupper, og da er sammenlikningen ikke tilgjengelig.
const pkBudgetTotals = (state, year, monthIndex) => {
  const lines = safeArray(state?.budgetsByYear?.[String(year)]?.lines);
  if (!lines.length) return null;
  const totals = {};
  for (const group of PK_ANALYTICS_GROUPS) totals[group.key] = { amount: 0, lines: 0 };
  let found = false;
  for (const line of lines) {
    const digit = String(line?.accountCode || '').trim().charAt(0);
    const section = line?.kind === 'income' ? 'inntekter' : 'kostnader';
    const group = PK_ANALYTICS_GROUPS.find((entry) => entry.section === section && entry.digits.includes(digit));
    if (!group) continue;
    const monthly = safeArray(line?.monthlyAmounts);
    const amount = monthly.length === 12 ? num(monthly[monthIndex]) : num(line?.annualAmount) / 12;
    totals[group.key].amount = pkRound(totals[group.key].amount + Math.abs(amount));
    totals[group.key].lines += 1;
    found = true;
  }
  return found ? totals : null;
};

const buildPeriodAnalytics = ({ periodId, state, basis = 'forrige_maned', explanations = {} } = {}) => {
  if (window.PlummeAccountingBasisCoverage.isHydratedStateBlocked(state)) {
    return {
      ...window.PlummeAccountingBasisCoverage.blockedReadModel('period_reconciliation_analytics', state.accountingBasisCoverage),
      basis,
      basisOptions: null,
      rows: null,
      margin: null,
    };
  }
  const range = pkPeriodRange(periodId);
  if (!range) return { basis, basisOptions: [], rows: [], margin: null };
  const current = pkGroupTotals(pkResultFor(state, range.year, range.monthIndex));
  const previous = range.monthIndex === 0
    ? { year: range.year - 1, monthIndex: 11 }
    : { year: range.year, monthIndex: range.monthIndex - 1 };
  const comparisons = {
    forrige_maned: { label: t('rcd.basis.previousMonth', 'Forrige måned'), totals: pkGroupTotals(pkResultFor(state, previous.year, previous.monthIndex)) },
    i_fjor: { label: t('rcd.basis.lastYear', 'Samme periode i fjor'), totals: pkGroupTotals(pkResultFor(state, range.year - 1, range.monthIndex)) },
    budsjett: { label: t('rcd.basis.budget', 'Budsjett'), totals: pkBudgetTotals(state, range.year, range.monthIndex) },
    // Prognosen finnes ikke i Plumme. Den står i lista fordi designet har den,
    // men den er avslått — ikke tom, som ville sett ut som «ingen avvik».
    prognose: { label: t('rcd.basis.forecast', 'Prognose'), totals: null },
  };
  const hasNumbers = (totals) => Boolean(totals) && Object.values(totals).some((entry) => entry.lines > 0);
  const basisOptions = Object.entries(comparisons).map(([key, entry]) => ({
    key, label: entry.label, available: hasNumbers(entry.totals),
  }));
  const active = hasNumbers(comparisons[basis]?.totals)
    ? basis
    : (basisOptions.find((option) => option.available)?.key || 'forrige_maned');
  const compare = comparisons[active]?.totals || null;
  const basisLabel = comparisons[active]?.label || '';

  const rows = PK_ANALYTICS_GROUPS
    .filter((group) => current[group.key].lines > 0 || Boolean(compare && compare[group.key].lines > 0))
    .map((group) => {
      const denne = current[group.key].amount;
      const sammenligning = compare ? compare[group.key].amount : null;
      const avvik = sammenligning === null ? null : pkRound(denne - sammenligning);
      const avvikPct = sammenligning ? pkRound((avvik / sammenligning) * 100) : null;
      return {
        key: group.key,
        label: group.label,
        denne,
        sammenligning,
        basisLabel,
        avvik,
        avvikPct,
        forklaring: explanations?.[group.key] || null,
      };
    });

  // Bruttofortjenesten har ingen egen forklaring. Den er omsetning minus
  // varekostnad, og faller ut av de to radene over — den vurderes sammen med
  // dem, ikke for seg.
  let margin = null;
  if (compare && current.omsetning.lines > 0) {
    const denneBrutto = pkRound(current.omsetning.amount - current.varekostnad.amount);
    const forrigeBrutto = pkRound(compare.omsetning.amount - compare.varekostnad.amount);
    const pct = (brutto, omsetning) => (omsetning ? pkRound((brutto / omsetning) * 100) : null);
    const denneMargin = pct(denneBrutto, current.omsetning.amount);
    const forrigeMargin = pct(forrigeBrutto, compare.omsetning.amount);
    margin = {
      denneBrutto,
      forrigeBrutto,
      denneMargin,
      forrigeMargin,
      endringPp: denneMargin === null || forrigeMargin === null ? null : pkRound(denneMargin - forrigeMargin),
      basisLabel,
    };
  }

  return { basis: active, basisOptions, rows, margin };
};

Object.assign(window, {
  buildPeriodCompleteness, buildPeriodAnalytics, pkMoney,
  pkPeriodRange, pkDateInMonth, pkIsManual, PK_ANALYTICS_GROUPS, PK_LATE_DAYS, pkLabeled,
});
